/**
 * Approximate map centroids per UDA.
 *
 * Keyed by ds_uda_lst_t.uda_id, which is what the JWT carries and what AuthContext
 * exposes as `user.udaId`. These ids are seeded and stable (3..24); the UDA code is
 * noted against each row so the mapping stays readable.
 *
 * The forms' Geo Location map used to open on a hardcoded Hyderabad centre — another
 * state entirely — so a user drawing a proposal boundary for Eluru had to pan across
 * Telangana first. There is no lat/lng on ds_uda_lst_t and no cheap boundary source
 * for a modal, so these are city centroids: close enough to open the map on the right
 * town, with the drawing tools doing the precise work.
 */
export const UDA_CENTROIDS: Record<number, [number, number]> = {
  3: [18.2949, 83.8938],  // SUDA     — Srikakulam
  4: [18.5667, 83.3667],  // BUDA     — Bobbili
  5: [17.6868, 83.2185],  // VMRDA    — Visakhapatnam
  6: [17.3500, 82.6500],  // VK-PCPIR — Visakhapatnam–Kakinada corridor
  7: [16.9891, 82.2475],  // KAUDA    — Kakinada
  8: [17.0005, 81.8040],  // RUDA     — Rajahmundry
  9: [16.5788, 82.0060],  // AMUDA    — Amalapuram
  10: [16.7107, 81.0952], // ELUDA    — Eluru
  11: [16.5150, 80.5180], // APCRDA   — Amaravati
  12: [16.1875, 81.1389], // MUDA     — Machilipatnam
  13: [15.5057, 80.0499], // ONUDA    — Ongole
  14: [14.4426, 79.9865], // NUDA     — Nellore
  15: [13.2172, 79.1003], // CHUDA    — Chittoor
  16: [13.6288, 79.4192], // TUDA     — Tirupati
  17: [15.8281, 78.0373], // KUDA     — Kurnool
  18: [14.1650, 77.8110], // PUDA     — Puttaparthi
  19: [13.5503, 78.5029], // PKMUDA   — Madanapalle
  20: [14.6819, 77.6006], // AHUDA    — Anantapur
  22: [16.2350, 79.2300], // PAUDA    — Palnadu (Narasaraopet)
  23: [15.9040, 80.4670], // BAUDA    — Bapatla
  24: [14.4674, 78.8241], // KDPUDA   — Kadapa
};

/** Andhra Pradesh, framed whole — the "All UDAs" / state-user view. */
export const AP_CENTER: [number, number] = [15.9129, 79.7400];

const UDA_ZOOM = 11;
const STATE_ZOOM = 7;

/**
 * Opening view for a map rendered inside a UDA-scoped screen.
 * Falls back to the state view for "All UDAs" (udaId 0), state users (null), and any
 * UDA seeded after this table was written.
 */
export function udaMapView(udaId?: number | null): { center: [number, number]; zoom: number } {
  const centroid = udaId ? UDA_CENTROIDS[Number(udaId)] : undefined;
  return centroid
    ? { center: centroid, zoom: UDA_ZOOM }
    : { center: AP_CENTER, zoom: STATE_ZOOM };
}
